import {
	createFileRoute,
	Navigate,
	notFound,
	useRouter,
} from "@tanstack/react-router"
import { useState } from "react"
import { useFriendsSuspense } from "@/entities/friend"
import { useCurrentUser } from "@/entities/user"
import { useDeclineMatch } from "@/features/easy-match"
import { FriendPreview } from "@/features/friend-preview"
import { FriendPersonalitySection } from "@/features/personality"
import { useGoBack } from "@/shared/lib/back-button"
import { PendingComponent } from "@/shared/ui/pending-component"
import { NewContactPreviewFooter } from "@/widgets/contact-list"

// The only route over friend-preview: a contact from the "New" tab. Match previews
// have no route of their own — they expand inside their notification.
export const Route = createFileRoute(
	"/_authenticated/new-contact/preview/$friendId",
)({
	component: NewContactPreviewWrapper,
	pendingComponent: PendingComponent,
	params: {
		parse: (raw) => {
			const friendId = Number(raw.friendId)
			if (!Number.isFinite(friendId)) throw notFound()
			return { friendId }
		},
		stringify: (parsed) => ({ friendId: String(parsed.friendId) }),
	},
})

/**
 * Prefers the friends cache, falls back to the router-state snapshot: a declined
 * contact leaves the cache, and without the snapshot the page would bounce to /home.
 */
function NewContactPreviewWrapper() {
	const { friendId } = Route.useParams()
	const { decline } = useDeclineMatch()
	const router = useRouter()
	const goBack = useGoBack({ to: "/home" })
	const user = useCurrentUser()

	const friendsQuery = useFriendsSuspense(user.id)
	const [snapshot] = useState(() => router.state.location.state.friend)

	const friend = friendsQuery.data.find((f) => f.id === friendId) ?? snapshot

	if (!friend) return <Navigate to="/home" replace />

	return (
		<FriendPreview
			friend={friend}
			personalitySlot={<FriendPersonalitySection friendId={friend.id} />}
			onBack={goBack}
			footer={
				<NewContactPreviewFooter
					friend={friend}
					onDecline={() => decline(friend)}
				/>
			}
		/>
	)
}
